import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  XMarkIcon,
  DocumentArrowDownIcon,
  DocumentTextIcon,
  CalendarIcon,
  UserGroupIcon,
  CogIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { excelReportsService } from '../../services/reports/excel-reports';
import { reportTemplatesService } from '../../services/reports/report-templates';
import { clientsService } from '../../services/logistics/clients';
import { driversService } from '../../services/logistics/drivers';
import { vehiclesService } from '../../services/logistics/vehicles';
import { useAuth } from '../../contexts/AuthContext';
import ReportTemplateModal from './ReportTemplateModal';
import LoadingSpinner from '../ui/LoadingSpinner';
import FirebaseDebugPanel from './FirebaseDebugPanel';
import { initializeReportTemplates } from '../../utils/firebase-setup';
import { formatDate } from '../../utils/formatting';

const ShipmentReportsModal = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { currentCompany, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [clients, setClients] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  
  // Default date range: last 30 days (user can change)
  const getDefaultDateRange = () => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 30);
    const toInput = (d) => d.toISOString().split('T')[0];
    return { startDate: toInput(start), endDate: toInput(end) };
  };

  // Report configuration
  const [reportConfig, setReportConfig] = useState(() => {
    const { startDate, endDate } = getDefaultDateRange();
    return {
      templateId: '',
      startDate,
      endDate,
      clientId: '',
      driverId: '',
      vehicleId: '',
      sortBy: 'date', // date, truckPlate, driver
      filename: 'shipments-report'
    };
  });

  // Load data
  useEffect(() => {
    if (isOpen && currentCompany?.id) {
      const { startDate, endDate } = getDefaultDateRange();
      setReportConfig(prev => ({ ...prev, startDate, endDate }));
      loadTemplates();
      loadClients();
      loadDrivers();
      loadVehicles();
    }
  }, [isOpen, currentCompany?.id]);

  const loadTemplates = async () => {
    try {
      const templatesData = await reportTemplatesService.getTemplates(currentCompany.id);
      setTemplates(templatesData || []);
      
      // Set default template if available
      const defaultTemplate = templatesData?.find(t => t.isDefault);
      if (defaultTemplate && !reportConfig.templateId) {
        setReportConfig(prev => ({ ...prev, templateId: defaultTemplate.id }));
      }
    } catch (error) {
      console.error('Error loading templates:', error);
      setTemplates([]); // Set empty array on error
      toast.error(t('reports.load_templates_error', 'Failed to load templates'));
    }
  };

  const loadClients = async () => {
    try {
      const clientsData = await clientsService.getClients(currentCompany.id);
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error(t('reports.load_clients_error', 'Failed to load clients'));
    }
  };

  const loadDrivers = async () => {
    try {
      const driversData = await driversService.getDrivers(currentCompany.id);
      setDrivers(driversData);
    } catch (error) {
      console.error('Error loading drivers:', error);
      toast.error(t('reports.load_drivers_error', 'Failed to load drivers'));
    }
  };

  const loadVehicles = async () => {
    try {
      const vehiclesData = await vehiclesService.getVehicles(currentCompany.id);
      setVehicles(vehiclesData);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error(t('reports.load_vehicles_error', 'Failed to load vehicles'));
    }
  };

  const handleGenerateReport = async () => {
    if (!currentCompany?.id) {
      toast.error(t('common.no_company_selected', 'No company selected'));
      return;
    }

    try {
      setGenerating(true);
      
      const options = {
        templateId: reportConfig.templateId || null,
        startDate: reportConfig.startDate ? new Date(reportConfig.startDate) : null,
        endDate: reportConfig.endDate ? new Date(reportConfig.endDate) : null,
        clientId: reportConfig.clientId || null,
        driverId: reportConfig.driverId || null,
        vehicleId: reportConfig.vehicleId || null,
        sortBy: reportConfig.sortBy || 'date',
      };

      console.log('🔄 Generating report with options:', options);
      
      const blob = await excelReportsService.generateShipmentReport(currentCompany.id, options);
      
      // Generate filename with date range
      let filename = reportConfig.filename || 'shipments-report';
      if (options.startDate || options.endDate) {
        const startStr = options.startDate ? options.startDate.toISOString().split('T')[0] : 'start';
        const endStr = options.endDate ? options.endDate.toISOString().split('T')[0] : 'end';
        filename += `_${startStr}_to_${endStr}`;
      }
      filename += '.xlsx';
      
      excelReportsService.downloadExcelFile(blob, filename);
      
      toast.success(t('reports.report_generated', 'Report generated successfully'));
      
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(t('reports.generate_error', 'Failed to generate report'));
    } finally {
      setGenerating(false);
    }
  };

  const handleCreateTemplate = async () => {
    // If no templates exist, try to initialize with default template
    if (templates.length === 0) {
      try {
        setLoading(true);
        const result = await initializeReportTemplates(currentCompany.id, user?.uid);
        if (result.success) {
          await loadTemplates(); // Reload templates
          toast.success(t('reports.default_template_created', 'Default template created successfully'));
          return;
        }
      } catch (error) {
        console.error('Failed to create default template:', error);
      } finally {
        setLoading(false);
      }
    }
    
    setEditingTemplate(null);
    setShowTemplateModal(true);
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm(t('reports.confirm_delete_template', 'Are you sure you want to delete this template?'))) {
      return;
    }

    try {
      setLoading(true);
      await reportTemplatesService.deleteTemplate(templateId);
      await loadTemplates();
      
      // Clear selection if deleted template was selected
      if (reportConfig.templateId === templateId) {
        setReportConfig(prev => ({ ...prev, templateId: '' }));
      }
      
      toast.success(t('reports.template_deleted', 'Template deleted successfully'));
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('reports.delete_template_error', 'Failed to delete template'));
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSaved = () => {
    loadTemplates();
    setShowTemplateModal(false);
    setEditingTemplate(null);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <DocumentArrowDownIcon className="w-6 h-6 text-green-600" />
              <h2 className="text-xl font-semibold text-gray-900">
                {t('reports.shipment_reports', 'Shipment Reports')}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              
              {/* Report Configuration */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <CogIcon className="w-5 h-5 mr-2 text-blue-600" />
                  {t('reports.report_configuration', 'Report Configuration')}
                </h3>
                
                <div className="space-y-4">
                  {/* Template Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.select_template', 'Select Template')}
                    </label>
                    <select
                      value={reportConfig.templateId}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, templateId: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="">{t('reports.no_template', 'No Template (Default Fields)')}</option>
                      {templates.map(template => (
                        <option key={template.id} value={template.id}>
                          {template.name} {template.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date Range */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('reports.start_date', 'Start Date')}
                      </label>
                      <input
                        type="date"
                        value={reportConfig.startDate}
                        onChange={(e) => setReportConfig(prev => ({ ...prev, startDate: e.target.value }))}
                        className="input-field w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('reports.end_date', 'End Date')}
                      </label>
                      <input
                        type="date"
                        value={reportConfig.endDate}
                        onChange={(e) => setReportConfig(prev => ({ ...prev, endDate: e.target.value }))}
                        className="input-field w-full"
                      />
                    </div>
                  </div>

                  {/* Client Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.filter_by_client', 'Filter by Client (Optional)')}
                    </label>
                    <select
                      value={reportConfig.clientId}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, clientId: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="">{t('reports.all_clients', 'All Clients')}</option>
                      {clients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.name || client.companyName}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Driver Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.filter_by_driver', 'Filter by Driver (Optional)')}
                    </label>
                    <select
                      value={reportConfig.driverId}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, driverId: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="">{t('reports.all_drivers', 'All Drivers')}</option>
                      {drivers.map(driver => (
                        <option key={driver.id} value={driver.id}>
                          {driver.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.filter_by_vehicle', 'Filter by Vehicle (Optional)')}
                    </label>
                    <select
                      value={reportConfig.vehicleId}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, vehicleId: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="">{t('reports.all_vehicles', 'All Vehicles')}</option>
                      {vehicles.map(vehicle => (
                        <option key={vehicle.id} value={vehicle.id}>
                          {vehicle.plateNumber}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Sort By */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.sort_by', 'Sort By')}
                    </label>
                    <select
                      value={reportConfig.sortBy}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, sortBy: e.target.value }))}
                      className="input-field w-full"
                    >
                      <option value="date">{t('reports.sort_by_date', t('common.date'))}</option>
                      <option value="truckPlate">{t('reports.sort_by_truck_plate', 'Truck Plate')}</option>
                      <option value="driver">{t('reports.sort_by_driver', 'Driver')}</option>
                    </select>
                  </div>

                  {/* Filename */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reports.filename', 'Filename')}
                    </label>
                    <input
                      type="text"
                      value={reportConfig.filename}
                      onChange={(e) => setReportConfig(prev => ({ ...prev, filename: e.target.value }))}
                      className="input-field w-full"
                      placeholder="shipments-report"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('reports.filename_help', 'Date range will be automatically added to filename')}
                    </p>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="mt-6">
                  <button
                    onClick={handleGenerateReport}
                    disabled={generating}
                    className="btn btn-primary w-full flex items-center justify-center"
                  >
                    {generating ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        {t('reports.generating', 'Generating...')}
                      </>
                    ) : (
                      <>
                        <DocumentArrowDownIcon className="w-5 h-5 mr-2" />
                        {t('reports.generate_excel', 'Generate Excel Report')}
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Template Management */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <DocumentTextIcon className="w-5 h-5 mr-2 text-purple-600" />
                    {t('reports.template_management', 'Template Management')}
                  </h3>
                  <button
                    onClick={handleCreateTemplate}
                    className="btn btn-secondary btn-sm flex items-center"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    {t('reports.create_template', 'Create Template')}
                  </button>
                </div>

                {loading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {templates.length === 0 ? (
                      <div>
                        <FirebaseDebugPanel onTemplatesCreated={loadTemplates} />
                        <div className="text-center py-8 text-gray-500">
                          <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                          <p>{t('reports.no_templates', 'No templates found')}</p>
                          <p className="text-sm mb-4">{t('reports.create_first_template', 'Create your first template to get started')}</p>
                          <button
                            onClick={handleCreateTemplate}
                            className="btn btn-primary btn-sm"
                          >
                            <PlusIcon className="w-4 h-4 mr-1" />
                            {t('reports.create_default_template', 'Create Default Template')}
                          </button>
                        </div>
                      </div>
                    ) : (
                      templates.map(template => (
                        <div key={template.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <h4 className="font-medium text-gray-900">{template.name}</h4>
                                {template.isDefault && (
                                  <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                    {t('reports.default', 'Default')}
                                  </span>
                                )}
                                {template.clientSpecific && (
                                  <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                    {t('reports.client_specific', 'Client Specific')}
                                  </span>
                                )}
                              </div>
                              {template.description && (
                                <p className="text-sm text-gray-600 mt-1">{template.description}</p>
                              )}
                              <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
                                <span>
                                  {Object.values(template.fields || {}).filter(f => f.enabled).length} fields enabled
                                </span>
                                <span>
                                  {t('reports.created', 'Created')}: {formatDate(template.createdAt?.toDate?.() || template.createdAt)}
                                </span>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleEditTemplate(template)}
                                className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title={t('common.edit', t('common.edit'))}
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              {!template.isDefault && (
                                <button
                                  onClick={() => handleDeleteTemplate(template.id)}
                                  className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title={t('common.delete', t('common.delete'))}
                                >
                                  <TrashIcon className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Help Section */}
            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">
                {t('reports.help_title', 'How to use Excel Reports')}
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• {t('reports.help_1', 'Create templates to customize which fields appear in your reports')}</li>
                <li>• {t('reports.help_2', 'Use date ranges to filter shipments by pickup date')}</li>
                <li>• {t('reports.help_3', 'Filter by specific clients for client-specific reports')}</li>
                <li>• {t('reports.help_4', 'Company information is automatically included at the top of each report')}</li>
                <li>• {t('reports.help_5', 'Templates can be client-specific for different reporting needs')}</li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              {t('common.close', t('common.close'))}
            </button>
          </div>
        </div>
      </div>

      {/* Template Modal */}
      <ReportTemplateModal
        isOpen={showTemplateModal}
        onClose={() => {
          setShowTemplateModal(false);
          setEditingTemplate(null);
        }}
        template={editingTemplate}
        onSave={handleTemplateSaved}
      />
    </>
  );
};

export default ShipmentReportsModal;